import "/@back@/wsp/actions/actions_Perms.js";
import {Action} from "/@lib@/commons/actions.js";
import {JPackBase, PackSrv, SKINPACK, WSPPACK} from "/@lib@/wsp/pack.js";
import {POPUP} from "/@back@/commons/widgets/popups.js";
import {JSX} from "/@lib@/commons/xml/dom.js";
import {IReg} from "/@lib@/commons/registry.js";


/**
 * Installation d'un wsp pack
 * accept : résolu via ctxt.accept, ou la pref du register "pack.action.install.accept" à défaut
 */

export class InstallWspPackAction extends Action<IInstallPackActionCtx> {
	protected defaultAccept = ".wsppack";
	protected installOnSrvPerm = "action.pack#install.wspPack";

	constructor(id?: string) {
		super(id || "installWspPack");
		this._label = "Installer...";
		this._description = "Installer un modèle documentaire ou une extension";
		this._group = "packs";
		this.setIcon("/@skin@/wsp/actions/installPack.svg");
	}

	async execute(ctx: IInstallPackActionCtx, ev?: Event): Promise<any | 'noPreventDefault' | 'noStopPropag' | 'noStopPropag-noPreventDefault'> {
		if (this.getId() === "installWspPack" && !await POPUP.confirm(<div>
			<p>{`Avez-vous confiance dans le fournisseur du pack que vous allez installer ?`}</p>
			<p>{"En terme de sécurité, installer un 'wsppack' revient à installer une nouvelle application dans votre système d'exploitation : un fournisseur malveillant pourrait y avoir injecté un virus et compromettre votre système et vos données."}</p>
		</div>, ctx.emitter, {
			titleBar: "Note de sécurité",
			okLbl: "J'ai confiance et j'installe",
			cancelLbl: "Je n'ai pas confiance et j'abandonne"
		})) return;
		// - Recherche des packSrv sur lesquels on possède le droit d'installer un pack
		let subPackSrvMap: Map<String, PackSrv> = new Map();
		ctx.packSrvList.forEach((packSrv) => {
			// ATTENTION : perm controlée sur le REG du packServer
			if (packSrv.universe.reg.hasPerm(this.installOnSrvPerm))
				subPackSrvMap.set(packSrv.universe.getId(), packSrv);
		});
		// - Selection des packs si ctx.packs non renseigné
		let files = ctx.packs;
		if (!files) {
			const input = document.createElement('input');
			input.type = "file";
			input.accept = (ctx.packExtensions ? ctx.packExtensions.join(";") : "") || this.defaultAccept;
			input.multiple = true;
			input.click();
			files = Array.from(await new Promise<FileList>((resolve) => {
				input.onchange = function (this: HTMLInputElement) {
					//console.log("input.onchange:::", this.files);
					resolve(this.files.length > 0 ? this.files : null);
				}
			}));
			if (!files || files.length == 0) return;
		}

		// - Proposition des cibles à l'utilisateur (si > 1)
		if (subPackSrvMap.size > 1) {
			const form = <form>
				<p>Veuillez sélectionner les environnements cibles dans lesquels réaliser l'installation :</p>
			</form> as HTMLFormElement;
			subPackSrvMap.forEach((packSrv) => {
				form.appendChild(<p>
					<label>
						<input type="checkbox" name={packSrv.universe.getId()} value={packSrv.universe.getId()} checked="true"/>
						{packSrv.universe.getName()}
					</label>
				</p>)
			});
			if (!await POPUP.confirm(form, ctx.emitter, {okLbl: "Installer", kind: "question"})) return;
			subPackSrvMap.forEach((packSrv) => {
				const inputElt = form.elements.namedItem(packSrv.universe.getId()) as HTMLInputElement;
				if (!inputElt || !inputElt.checked)
					subPackSrvMap.delete(packSrv.universe.getId());
			})
		}
		// - Installation
		if (ctx.globalInstallHandler)
			await ctx.globalInstallHandler("start", false);
		try {
			if (subPackSrvMap.size > 0) {
				await Promise.all(Array.from(subPackSrvMap).map(async (value) => {
					let packSrv = value[1];
					await Promise.all(files.map(async (packFile) => {
						//console.log("Install pack "+packFile.name + " in "+packSrv.universe.getName());
						if (ctx.packInstallHandler)
							await ctx.packInstallHandler("start", packFile, packSrv);
						let pack;
						try {
							pack = await this.doInstallPackOnSrv(packSrv, packFile);
						} finally {
							if (ctx.packInstallHandler)
								await ctx.packInstallHandler("end", packFile, packSrv, pack);
						}
					}));
				}));
			}
		} finally {
			if (ctx.globalInstallHandler)
				await ctx.globalInstallHandler("end", subPackSrvMap.size > 0);
		}
	}

	async doInstallPackOnSrv(packSrv: PackSrv, packFile: File): Promise<JPackBase> {
		return WSPPACK.installPack(packSrv, packFile);
	}

	isVisible(ctx: IInstallPackActionCtx): boolean {
		if (ctx.packSrvList && ctx.packSrvList.findIndex((packSrv) => {
			return packSrv.universe.reg.hasPerm(this.installOnSrvPerm);
		}) == -1) return false;
		return super.isVisible(ctx);
	}

	static SINGLETON = new InstallWspPackAction();
}

/**
 * Installation d'un wsp pack
 * accept : résolu via ctxt.accept, ou la pref du register "pack.action.install.accept" à défaut
 */

export class InstallSkinPackAction extends InstallWspPackAction {
	protected defaultAccept = ".skinpack";
	protected installOnSrvPerm = "action.pack#install.skinPack";

	constructor(id?: string) {
		super(id || "installSkinPack");
		this._label = "Installer...";
		this._description = "Installer un habillage graphique";
		this._group = "packs";
		this.setIcon("/@skin@/wsp/actions/installPack.svg");
	}

	async doInstallPackOnSrv(packSrv: PackSrv, packFile: File): Promise<JPackBase> {
		return SKINPACK.install(packSrv, packFile);
	}

	static SINGLETON = new InstallSkinPackAction();

}


export interface IInstallPackActionCtx {
	reg: IReg<any>,
	/** Liste pré-définie de packs à installer **/
	packs?: File[],
	packSrvList: PackSrv[],
	emitter?: HTMLElement,

	/** extensions autorisées pour l'installation (avec le .) **/
	packExtensions?: string[],

	/** Suivi de chaque installations [pack,server]
	 * Cette méthode est appelée deux fois :
	 * 	- avant l'appel du service d'installation (@param state = "start")
	 * 	- aprés l'appel du service (@param state = "end")
	 * @param pack : ne peut être prédent que si state="end"
	 ***/
	packInstallHandler?(state: "start" | "end", file: File, packSrv: PackSrv, pack?: JPackBase): Promise<void>,

	/** Suivi global de l'installation
	 * Cette méthode est appelée deux fois :
	 * 	- avant l'appel du service d'installation (@param state = "start")
	 * 	- aprés l'appel du service (@param state = "end")
	 * @param pack : ne peut être prédent que si state="end"
	 ***/
	globalInstallHandler?(state: "start" | "end", packsAreInstalled: boolean): Promise<void>,
}
